// src/pages/BillingBook.js
import React, { useState, useEffect } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
  useMediaQuery, useTheme, MenuItem, IconButton, Badge, Alert, Chip, RadioGroup,
  Stack,
  Radio, FormControlLabel
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  HelpOutline as HelpIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon
} from '@mui/icons-material';
import { apiUrl, OTP_API_URL } from '../config/api';

const API_URL           = apiUrl('save_billing_book');
const GET_ADV_API       = apiUrl('get_advance_salary');
const SAVE_ADV_API      = apiUrl('save_advance_salary');
const DELETE_ADV_API    = apiUrl('delete_advance_salary');
const GET_CASH_API      = apiUrl('get_cash_count');
const SAVE_CASH_API     = apiUrl('save_cash_count');
const STAFF_API         = apiUrl('get_staff');

// Purchases (items + stock add)
const PURCHASE_ITEMS_API = apiUrl('purchase_items');
const PURCHASE_STOCK_API = apiUrl('purchase_stock');

// OTP API endpoint you already have
const OTP_API           = OTP_API_URL;

// helper to format "2024-05-02" → "2nd of May 2024"
function formatDateVerbose(isoDate) {
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return '';
  const ord = n => {
    if (n % 100 >= 11 && n % 100 <= 13) return 'th';
    if (n % 10 === 1) return 'st';
    if (n % 10 === 2) return 'nd';
    if (n % 10 === 3) return 'rd';
    return 'th';
  };
  const monthName = new Date(y, m - 1).toLocaleString('default', { month: 'long' });
  return `${d}${ord(d)} of ${monthName} ${y}`;
}

const DESCRIPTION_PRESETS = [
  "Amex Card", "Credit Return", "Credit Sale", "DCC",
  "Master/VISA Card", "Morning Purchase", "Purchase",
  "Website", "PayHere",
];

const toArray = (value) => (Array.isArray(value) ? value : []);
const visibleEntryDescription = (desc) => String(desc || '').replace(/^Advance #\d+\s+for\s+/i, 'Advance for ');

// ────────────────────────────────────────────────────────────────────────────
// Reusable Confirmation + OTP dialog (NO phone field)
function ConfirmDeleteDialog({ open, req, onClose }) {
  const [phase, setPhase] = React.useState('confirm'); // 'confirm' | 'otp_sent'
  const [otp, setOtp]     = React.useState('');
  const [busy, setBusy]   = React.useState(false);
  const [err, setErr]     = React.useState('');

  const key = req ? `otp_token_${req.type}_${req.refId}` : null;

  React.useEffect(() => {
    if (open) { setPhase('confirm'); setOtp(''); setBusy(false); setErr(''); }
  }, [open, req]);

  const sendOtp = async () => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(OTP_API, {
        method: 'POST',
        headers: {'Content-Type':'application/json'},
        body: JSON.stringify({
          step: 'send',
          delete_type: req.type,
          delete_ref:  String(req.refId),
          details: req.details,
        })
      }).then(r=>r.json());
      if (r.status !== 'ok') throw new Error(r.message || 'Failed to send OTP');
      localStorage.setItem(key, r.token);
      setPhase('otp_sent');
    } catch(e){ setErr(e.message); }
    finally{ setBusy(false); }
  };

  const verifyOtp = async () => {
    setBusy(true); setErr('');
    try {
      const token = localStorage.getItem(key);
      if (!token) throw new Error('Missing OTP token. Please resend.');
      const r = await fetch(OTP_API, {
        method: 'POST',
        headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ step:'verify', token, otp })
      }).then(r=>r.json());
      if (r.status !== 'ok') throw new Error(r.message || 'OTP verification failed');
      localStorage.removeItem(key);
      await req.onVerified();
      onClose();
    } catch(e){ setErr(e.message); }
    finally{ setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth>
      <DialogTitle>Confirm Delete</DialogTitle>
      <DialogContent sx={{ display:'flex', flexDirection:'column', gap:2 }} dividers>
        <Typography color="error">
          You are about to delete: <strong>{req?.type}</strong>
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace:'pre-wrap' }}>{req?.details}</Typography>

        {phase === 'confirm' && (
          <Typography variant="body2">Click continue to send an OTP to the registered number.</Typography>
        )}

        {phase === 'otp_sent' && (
          <>
            <Typography variant="body2">Enter the OTP sent to your registered number:</Typography>
            <TextField
              label="4-digit OTP"
              value={otp}
              onChange={e=>setOtp(e.target.value.replace(/\D+/g,''))}
              inputProps={{ maxLength: 4 }}
              size="small"
            />
            {!!err && <Typography color="error">{err}</Typography>}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        {phase === 'confirm' ? (
          <Button onClick={sendOtp} disabled={busy} variant="contained" color="error">
            {busy ? 'Sending…' : 'Send OTP & Continue'}
          </Button>
        ) : (
          <Button onClick={verifyOtp} disabled={busy || otp.length !== 4} variant="contained">
            {busy ? 'Verifying…' : 'Verify & Delete'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Purchase Dialog
function PurchaseDialog({ open, onClose, billDate, onAdded }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems]     = useState([]); // {id, category, name, active}
  const [categories, setCategories] = useState([]);

  const [form, setForm] = useState({
    category: '',
    item_id: '',
    item_name: '',
    unit: 'kg',     // kg | g | pc
    qty: '',
    total_amount: '',
    notes: ''
  });

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetch(`${PURCHASE_ITEMS_API}?active=1&limit=1000`)
      .then(r=>r.json())
      .then(d => {
        const arr = Array.isArray(d.items) ? d.items : [];
        setItems(arr);
        const cats = Array.from(new Set(arr.map(x => x.category))).sort((a,b)=>a.localeCompare(b));
        setCategories(cats);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  const filteredItems = form.category
    ? items.filter(x => String(x.category) === String(form.category))
    : [];

  const selectedItem = filteredItems.find(x => String(x.id) === String(form.item_id));

  const onChange = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const savePurchase = async () => {
    // validate
    if (!form.category)    return alert('Pick a category');
    if (!form.item_id)     return alert('Pick an item');
    if (!form.unit)        return alert('Pick a unit');
    const qty   = parseFloat(form.qty);
    const total = parseFloat(form.total_amount);
    if (!(qty > 0))        return alert('Enter a valid quantity');
    if (!(total > 0))      return alert('Enter a valid total amount');

    const payload = {
      bill_date:    billDate,
      item_id:      parseInt(form.item_id, 10),
      category:     form.category,
      item_name:    selectedItem?.name || form.item_name,
      unit:         form.unit,
      qty:          qty,
      total_amount: total,
      notes:        form.notes || null
    };

    try {
      const res = await fetch(PURCHASE_STOCK_API, {
        method: 'POST',
        headers: { 'Content-Type':'application/json' },
        body: JSON.stringify(payload)
      }).then(r=>r.json());
      if (res.status !== 'ok') {
        alert(res.message || 'Failed to save purchase'); return;
      }

      const unitTxt = form.unit === 'pc' ? 'pcs' : form.unit;
      const unitPrice = qty > 0 ? (total / qty) : 0;
      const desc = `Purchase: ${form.category} > ${selectedItem?.name} — ${qty} ${unitTxt} @ Rs ${unitPrice.toFixed(2)}`;
      const purchaseId = Number(res?.id || 0);

      // Push into entries as an expense line
      onAdded({
        type: 'expense',
        amount: String(total),
        description: desc,
        ...(purchaseId > 0 ? { source_type: 'stock_purchase', source_id: purchaseId } : {}),
      });

      // reset
      setForm({
        category: '',
        item_id: '',
        item_name: '',
        unit: 'kg',
        qty: '',
        total_amount: '',
        notes: ''
      });
      onClose();
    } catch {
      alert('Network error');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Add Purchase (Stock)</DialogTitle>
      <DialogContent dividers sx={{ display:'flex', flexDirection:'column', gap:2 }}>
        {loading && <CircularProgress size={22} />}

        <TextField
          select label="Category" value={form.category}
          onChange={e => onChange('category', e.target.value)}
          fullWidth
        >
          {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>

        <TextField
          select label="Item" value={form.item_id}
          onChange={e => onChange('item_id', e.target.value)}
          fullWidth
          disabled={!form.category}
        >
          {filteredItems.map(it => (
            <MenuItem key={it.id} value={it.id}>{it.name}</MenuItem>
          ))}
        </TextField>

        <Box>
          <Typography variant="subtitle2" gutterBottom>Unit</Typography>
          <RadioGroup
            row
            value={form.unit}
            onChange={e => onChange('unit', e.target.value)}
          >
            <FormControlLabel value="kg" control={<Radio />} label="Kg" />
            <FormControlLabel value="g"  control={<Radio />} label="Gram (g)" />
            <FormControlLabel value="pc" control={<Radio />} label="Piece" />
          </RadioGroup>
        </Box>

        <Box sx={{ display:'flex', gap:2, flexWrap:'wrap' }}>
          <TextField
            label={form.unit === 'pc' ? 'Quantity (pcs)' : (form.unit === 'g' ? 'Quantity (g)' : 'Quantity (kg)')}
            type="number"
            value={form.qty}
            onChange={e => onChange('qty', e.target.value)}
          />
          <TextField
            label="Total Amount (Rs)"
            type="number"
            value={form.total_amount}
            onChange={e => onChange('total_amount', e.target.value)}
          />
          {parseFloat(form.qty) > 0 && parseFloat(form.total_amount) > 0 && (
            <Box sx={{ alignSelf:'center' }}>
              <Typography variant="body2" color="text.secondary">
                Unit Price: Rs {(parseFloat(form.total_amount) / parseFloat(form.qty)).toFixed(2)} / {form.unit === 'pc' ? 'pc' : form.unit}
              </Typography>
            </Box>
          )}
        </Box>

        <TextField
          label="Notes (optional)"
          value={form.notes}
          onChange={e => onChange('notes', e.target.value)}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={savePurchase} variant="contained" startIcon={<ShoppingCartIcon/>}>
          Add Purchase
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Main component
export default function BillingBook() {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fmt = num => Number(num || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2
  });

  const typeLabel = t => {
    if (t === 'card')    return 'Card / Online';
    if (t === 'expense') return 'Subtract';
    if (t === 'income')  return 'Add';
    return t;
  };

  // State …
  const [billDate, setBillDate]                       = useState(new Date().toISOString().split('T')[0]);
  const [totalSalesToday, setTotalSalesToday]         = useState('');
  const [totalTips, setTotalTips]                     = useState('');
  const [totalServiceCharges, setTotalServiceCharges] = useState('');
  const [entries, setEntries]                         = useState([]);
  const [advances, setAdvances]                       = useState([]);
  const [staffList, setStaffList]                     = useState([]);
  const [advListOpen, setAdvListOpen]                 = useState(false);

  const [advEditOpen, setAdvEditOpen] = useState(false);
  const [advEditId,   setAdvEditId]   = useState(null);
  const [newAdvance,  setNewAdvance]  = useState({ staff_id: '', amount: '' });

  const [entryModalOpen, setEntryModalOpen]           = useState(false);
  const [entryEditIndex, setEntryEditIndex]           = useState(-1);
  const [newEntry, setNewEntry] = useState({
    type: 'income', amount: '', description: '', isOther: false,
  });

  const [loading, setLoading]         = useState(false);
  const [actualCash, setActualCash]   = useState('');
  const [cashDiff, setCashDiff]       = useState(0);
  const [recordSaved, setRecordSaved] = useState(false);
  const [dirty, setDirty]             = useState(false);
  const [savingRecord, setSavingRecord] = useState(false);
  const [savingCash, setSavingCash]   = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const [confirmReq, setConfirmReq] = useState(null);

  // NEW: purchase dialog open
  const [purchaseOpen, setPurchaseOpen] = useState(false);

  // Load per date …
  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}?summary_date=${billDate}`)
      .then(r => r.json())
      .then(data => {
        if (data.bill_date) {
          setTotalSalesToday(data.total_sales_today);
          setTotalTips(data.total_tips || '');
          setTotalServiceCharges(data.total_service_charges || '');
          setEntries(data.entries ? JSON.parse(data.entries) : []);
          setRecordSaved(true);
          setDirty(false);
          setLastSavedAt(data.updated_at || null);
        } else {
          setTotalSalesToday('');
          setTotalTips('');
          setTotalServiceCharges('');
          setEntries([]);
          setRecordSaved(false);
          setDirty(false);
          setLastSavedAt(null);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    fetch(`${GET_ADV_API}?date=${billDate}`)
      .then(r => r.json())
      .then(d => setAdvances(toArray(d)))
      .catch(() => {});

    fetch(`${GET_CASH_API}?date=${billDate}`)
      .then(r => r.json())
      .then(d => setActualCash(d.actual_cash != null ? String(d.actual_cash) : ''))
      .catch(() => setActualCash(''));
  }, [billDate]);

  useEffect(() => {
    fetch(STAFF_API)
      .then(r => r.json())
      .then(d => setStaffList(toArray(d)))
      .catch(() => {});
  }, []);

  const refTotal   = parseFloat(totalSalesToday) || 0;
  const incTotal   = entries.filter(e => e.type === 'income').reduce((s, e) => s + parseFloat(e.amount || 0), 0);
  const expAll     = entries.filter(e => e.type === 'expense' || e.type === 'card').reduce((s, e) => s + parseFloat(e.amount || 0), 0);
  const safeAdvances = toArray(advances);
  const advTotal   = safeAdvances.reduce((s, a) => s + parseFloat(a.amount || 0), 0);
  const expNonAdv  = expAll - advTotal;
  const revenue    = refTotal - expNonAdv + incTotal;
  const netRevenue = revenue - advTotal;

  useEffect(() => {
    const ac = parseFloat(actualCash) || 0;
    setCashDiff(ac - netRevenue);
  }, [actualCash, netRevenue]);

  const markDirty = () => setDirty(true);

  const saveRecord = async (silent=false) => {
    setSavingRecord(true);
    try {
      const payload = {
        bill_date:             billDate,
        total_sales_today:     refTotal,
        total_tips:            parseFloat(totalTips) || 0,
        total_service_charges: parseFloat(totalServiceCharges) || 0,
        entries,
        cash_on_cashier:       refTotal + incTotal - expNonAdv - advTotal
      };
      const d = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).then(r=>r.json());
      if (d.status === 'success') {
        setRecordSaved(true);
        setDirty(false);
        setLastSavedAt(new Date().toISOString());
        if (!silent) alert("Saved successfully");
        return true;
      } else {
        if (!silent) alert("Error: " + d.message);
        return false;
      }
    } catch {
      if (!silent) alert("Network error");
      return false;
    } finally {
      setSavingRecord(false);
    }
  };

  const saveCashCount = async () => {
    if (actualCash === '') { alert("Enter actual cash"); return; }
    if (!recordSaved || dirty) {
      const confirm = window.confirm("You must save the Billing Record first. Save now and then save Cash Count?");
      if (!confirm) return;
      const ok = await saveRecord(true);
      if (!ok) return;
    }
    setSavingCash(true);
    try {
      const d = await fetch(SAVE_CASH_API, {
        method: 'POST',
        headers: { 'Content-Type':'application/json' },
        body: JSON.stringify({ date: billDate, actual_cash: parseFloat(actualCash) })
      }).then(r=>r.json());
      if (d.status === 'success') alert("Cash count saved");
      else alert("Error: " + d.message);
    } catch {
      alert("Network error");
    } finally {
      setSavingCash(false);
    }
  };

  const saveAllNow = async () => {
    const ok = await saveRecord(true);
    if (!ok) return;
    await saveCashCount();
  };

  // Advances …
  const saveAdvance = () => {
    if (!newAdvance.staff_id || !newAdvance.amount) return alert("Select staff & amount");
    const payload = {
      id:           advEditId,
      action:       advEditId ? 'update' : 'add',
      advance_date: billDate,
      staff_id:     parseInt(newAdvance.staff_id, 10),
      amount:       parseFloat(newAdvance.amount)
    };
    fetch(SAVE_ADV_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    .then(r => r.json())
    .then(d => {
      if (d.status !== 'success') return alert("Error: " + d.message);
      fetch(`${GET_ADV_API}?date=${billDate}`)
        .then(r => r.json())
        .then(rows => setAdvances(toArray(rows)));

      const staffName = staffList.find(s => s.id === payload.staff_id)?.name || 'Staff';
      const advanceId = advEditId || d.id;
      const descBase  = `Advance #${advanceId} for ${staffName}`;

      if (!advEditId) {
        setEntries(es => [ ...es, { type: 'expense', amount: String(payload.amount), description: descBase } ]);
      } else {
        setEntries(es => es.map(e =>
          e.description.startsWith(`Advance #${advanceId} `)
            ? { ...e, amount: String(payload.amount), description: descBase }
            : e
        ));
      }
      setAdvEditOpen(false);
      setAdvEditId(null);
      setNewAdvance({ staff_id: '', amount: '' });
      markDirty();
    })
    .catch(() => alert("Network error"));
  };

  const askDeleteAdvance = (a) => {
    setConfirmReq({
      type: 'advance',
      refId: a.id,
      details: `Advance for ${a.staff_name}\nAmount: Rs ${fmt(a.amount)}\nDate: ${billDate}`,
      onVerified: async () => {
        const r = await fetch(DELETE_ADV_API, {
          method: 'POST',
          headers: { 'Content-Type':'application/json' },
          body: JSON.stringify({ action:'delete', id: a.id })
        }).then(r=>r.json());
        if (r.status !== 'success') { alert("Error: " + r.message); return; }
        setAdvances(prev => toArray(prev).filter(x => x.id !== a.id));
        setEntries(es => es.filter(e => !e.description.startsWith(`Advance #${a.id} `)));
        markDirty();
      }
    });
  };

  const askDeleteEntry = (i) => {
    const e = entries[i];
    setConfirmReq({
      type: 'entry',
      refId: i,
      details: `Entry: ${e.description}\nType: ${typeLabel(e.type)}\nAmount: Rs ${fmt(e.amount)}\nDate: ${billDate}`,
      onVerified: async () => {
        setEntries(es => es.filter((_, idx) => idx !== i));
        markDirty();
      }
    });
  };

  // Entry modal
  const openEntry = idx => {
    if (idx >= 0) {
      const e = entries[idx];
      const isPreset = DESCRIPTION_PRESETS.includes(e.description);
      setNewEntry({
        type:        e.type,
        amount:      String(e.amount),
        description: isPreset ? e.description : e.description,
        isOther:     !isPreset
      });
      setEntryEditIndex(idx);
    } else {
      setNewEntry({ type: 'income', amount: '', description: '', isOther: false });
      setEntryEditIndex(-1);
    }
    setEntryModalOpen(true);
  };
  const closeEntry = () => setEntryModalOpen(false);

  const saveEntry = () => {
    if (!newEntry.amount) return alert("Enter amount");
    if (!newEntry.description.trim()) return alert("Enter description");
    const upd = [...entries];
    const existing = entryEditIndex >= 0 ? (upd[entryEditIndex] || {}) : {};
    const obj = {
      ...existing,
      type:        newEntry.type,
      amount:      newEntry.amount,
      description: newEntry.description
    };
    if (entryEditIndex >= 0) upd[entryEditIndex] = obj;
    else upd.push(obj);
    setEntries(upd);
    markDirty();
    closeEntry();
  };

  // handy fn passed into PurchaseDialog
  const addEntryFromPurchase = (entryObj) => {
    setEntries(es => [...es, entryObj]);
    markDirty();
  };

  const saveStateLabel = !recordSaved ? 'Not saved' : dirty ? 'Changes not saved' : 'Saved';
  const saveStateColor = !recordSaved ? 'default' : dirty ? 'warning' : 'success';

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>Billing Book</Typography>

      {/* Date + status */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap:'wrap' }}>
        <TextField
          label="Date"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={billDate}
          onChange={e => setBillDate(e.target.value)}
        />
        <Button startIcon={<RefreshIcon />} variant="contained" onClick={() => setBillDate(billDate)}>
          Refresh
        </Button>
        <Typography variant="body2" sx={{ ml: 1 }}>
          {formatDateVerbose(billDate)}
        </Typography>
        <Chip label={`Bill Status: ${saveStateLabel}`} color={saveStateColor} variant="filled" sx={{ ml:'auto' }} />
      </Box>

      {loading && <CircularProgress />}

      {/* Overview */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {[
          { label:'Sales (ref)', value: parseFloat(totalSalesToday)||0, caption: null },
          { label:'Additions', value: entries.filter(e=>e.type==='income').reduce((s,e)=>s+parseFloat(e.amount||0),0), caption: null },
          { label:'Subtractions', value: entries.filter(e=>e.type==='expense'||e.type==='card').reduce((s,e)=>s+parseFloat(e.amount||0),0), caption: null },
          { label:'Revenue', value: (parseFloat(totalSalesToday)||0) - (entries.filter(e=>e.type==='expense'||e.type==='card').reduce((s,e)=>s+parseFloat(e.amount||0),0) - safeAdvances.reduce((s,a)=>s+parseFloat(a.amount||0),0)) + entries.filter(e=>e.type==='income').reduce((s,e)=>s+parseFloat(e.amount||0),0), caption: null },
          { label:'Advances', value: safeAdvances.reduce((s,a)=>s+parseFloat(a.amount||0),0), caption: null }
        ].map(stat => (
          <Box key={stat.label} sx={{ minWidth: 140 }}>
            <Typography variant="subtitle2">{stat.label}</Typography>
            <Typography variant="h6">Rs {fmt(stat.value)}</Typography>
          </Box>
        ))}
      </Paper>

      {/* Reference & extras */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Box>
          <Typography>Total Sales</Typography>
          <TextField
            variant="standard"
            type="number"
            value={totalSalesToday}
            onChange={e => { setTotalSalesToday(e.target.value); setDirty(true); }}
          />
        </Box>

        <Box>
          <Typography>Total Tips</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              variant="standard" type="number" value={totalTips}
              onChange={e => { setTotalTips(e.target.value); setDirty(true); }}
            />
            <Button
              onClick={() => {
                if (!totalTips || isNaN(parseFloat(totalTips))) return;
                setEntries(es => [ ...es, { type: 'income', amount: totalTips, description: 'Total Tips' } ]);
                setDirty(true);
              }}
              variant="outlined"
              size="small"
            >
              Add to Entries
            </Button>
          </Box>
        </Box>

        <Box>
          <Typography>Service Charges</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              variant="standard" type="number" value={totalServiceCharges}
              onChange={e => { setTotalServiceCharges(e.target.value); setDirty(true); }}
            />
            <Button
              onClick={() => {
                if (!totalServiceCharges || isNaN(parseFloat(totalServiceCharges))) return;
                setEntries(es => [ ...es, { type: 'income', amount: totalServiceCharges, description: 'Service Charges' } ]);
                setDirty(true);
              }}
              variant="outlined"
              size="small"
            >
              Add to Entries
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Entries */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap:1, flexWrap:'wrap' }}>
          <Typography variant="h6">Entries</Typography>
          <Box sx={{ display:'flex', gap:1 }}>
            <Button onClick={() => setPurchaseOpen(true)} variant="outlined" startIcon={<ShoppingCartIcon/>}>
              Add Purchase
            </Button>
            <Button onClick={() => openEntry(-1)} variant="contained">
              Add Entry
            </Button>
          </Box>
        </Box>

        {entries.length === 0
          ? <Typography sx={{ mt: 1 }}>No entries</Typography>
          : isMobile ? (
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {entries.map((e, i) => (
                <Paper key={i} sx={{ p: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Chip size="small" variant="outlined" label={i + 1} />
                    <Typography>{visibleEntryDescription(e.description)} ({typeLabel(e.type)})</Typography>
                  </Stack>
                  <Typography>Rs {fmt(e.amount)}</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button size="small" onClick={() => openEntry(i)}>Edit</Button>
                    <Button size="small" color="error" onClick={() => askDeleteEntry(i)}>Delete</Button>
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={64}>No.</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((e, i) => (
                  <TableRow key={i}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{typeLabel(e.type)}</TableCell>
                    <TableCell>{visibleEntryDescription(e.description)}</TableCell>
                    <TableCell>Rs {fmt(e.amount)}</TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => openEntry(i)}>Edit</Button>
                      <Button size="small" color="error" onClick={() => askDeleteEntry(i)}>Delete</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        }
      </Paper>

      {/* Advances */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={() => setAdvEditOpen(true)}>Add Advance</Button>
          <IconButton onClick={() => setAdvListOpen(true)}>
            <Badge badgeContent={safeAdvances.length} color="primary">
              <HelpIcon />
            </Badge>
          </IconButton>
        </Box>
      </Paper>

      {/* Advance list */}
      <Dialog open={advListOpen} onClose={() => setAdvListOpen(false)} fullWidth>
        <DialogTitle>Advances on {billDate}</DialogTitle>
        <DialogContent dividers>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={64}>No.</TableCell>
                <TableCell>Staff</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {safeAdvances.map((a, index) => (
                <TableRow key={a.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{a.staff_name}</TableCell>
                  <TableCell>Rs {fmt(a.amount)}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setAdvEditId(a.id);
                        setNewAdvance({ staff_id: String(a.staff_id), amount: String(a.amount) });
                        setAdvEditOpen(true);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => askDeleteAdvance(a)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!safeAdvances.length && (
                <TableRow><TableCell colSpan={4} align="center">No advances</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions><Button onClick={() => setAdvListOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      {/* Add/Edit Advance */}
      <Dialog
        open={advEditOpen}
        onClose={() => { setAdvEditOpen(false); setAdvEditId(null); }}
        fullWidth
      >
        <DialogTitle>{advEditId ? 'Edit Advance' : 'Add Advance'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            select label="Staff" variant="standard" value={newAdvance.staff_id}
            onChange={e => setNewAdvance(a => ({ ...a, staff_id: e.target.value }))}
          >
            {staffList.map(s => (<MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>))}
          </TextField>
          <TextField
            label="Amount" type="number" variant="standard" value={newAdvance.amount}
            onChange={e => setNewAdvance(a => ({ ...a, amount: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAdvEditOpen(false); setAdvEditId(null); }}>Cancel</Button>
          <Button onClick={saveAdvance} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Save sections */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:2, flexWrap:'wrap' }}>
          <Chip label="Step 1" />
          <Typography variant="h6">Save Billing Records</Typography>
          {lastSavedAt && (
            <Typography variant="caption" sx={{ ml: 'auto' }}>
              Last saved: {new Date(lastSavedAt).toLocaleString()}
            </Typography>
          )}
        </Box>
        <Alert severity={dirty ? 'warning' : (recordSaved ? 'success' : 'info')} sx={{ mt:2, mb:2 }}>
          {dirty
            ? 'You have unsaved changes. Please save the billing record before saving cash count.'
            : recordSaved
              ? 'Billing record is saved for this date.'
              : 'No saved billing record yet for this date.'
          }
        </Alert>
        <Button variant="contained" onClick={() => saveRecord(false)} disabled={savingRecord}>
          {savingRecord ? 'Saving…' : 'Save Full Records'}
        </Button>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:2, flexWrap:'wrap' }}>
          <Chip label="Step 2" color="primary" />
          <Typography variant="h6">Cash Count</Typography>
        </Box>

        {!recordSaved || dirty ? (
          <Alert severity="warning" sx={{ mt:2, mb:2 }}>
            Save Step 1 first. You can click <strong>Save All Now</strong> to do both in order.
          </Alert>
        ) : (
          <Alert severity="success" sx={{ mt:2, mb:2 }}>
            Billing record is saved. You can safely save the cash count.
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Actual Cash" type="number" variant="standard" value={actualCash}
            onChange={e => setActualCash(e.target.value)}
          />
          <Typography>
            Difference:&nbsp;
            <Box component="span" sx={{ color: (parseFloat(actualCash)||0) - netRevenue >= 0 ? 'green' : 'red', fontWeight:'bold' }}>
              Rs {fmt((parseFloat(actualCash)||0) - netRevenue)} {((parseFloat(actualCash)||0) - netRevenue) >= 0 ? '(Extra)' : '(Due)'}
            </Box>
          </Typography>

          <Button variant="outlined" onClick={saveAllNow} disabled={savingRecord || savingCash}>
            {savingRecord || savingCash ? 'Working…' : 'Save All Now (Bill → Cash)'}
          </Button>

          <Button variant="contained" onClick={saveCashCount} disabled={!recordSaved || dirty || savingCash}>
            {savingCash ? 'Saving…' : 'Save Cash Count'}
          </Button>
        </Box>
      </Paper>

      {/* OTP dialog */}
      <ConfirmDeleteDialog open={!!confirmReq} req={confirmReq} onClose={() => setConfirmReq(null)} />

      {/* Entry modal */}
      <Dialog open={entryModalOpen} onClose={closeEntry} fullWidth>
        <DialogTitle>{entryEditIndex >= 0 ? 'Edit Entry' : 'Add Entry'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            select label="Type" variant="standard" value={newEntry.type}
            onChange={e => setNewEntry(ne => ({ ...ne, type: e.target.value }))}
          >
            <MenuItem value="income">Add</MenuItem>
            <MenuItem value="expense">Subtract</MenuItem>
            <MenuItem value="card">Card/Online</MenuItem>
            <MenuItem value="none">None</MenuItem>
          </TextField>

          <TextField
            select label="Description" variant="standard"
            value={newEntry.isOther ? 'other' : newEntry.description}
            onChange={e => {
              const v = e.target.value;
              if (v === 'other') setNewEntry(ne => ({ ...ne, isOther: true, description: '' }));
              else setNewEntry(ne => ({ ...ne, isOther: false, description: v }));
            }}
          >
            {DESCRIPTION_PRESETS.map(opt => (<MenuItem key={opt} value={opt}>{opt}</MenuItem>))}
            <MenuItem value="other">Other</MenuItem>
          </TextField>

          {newEntry.isOther && (
            <TextField
              label="Enter description" variant="standard" value={newEntry.description}
              onChange={e => setNewEntry(ne => ({ ...ne, description: e.target.value }))}
              fullWidth
            />
          )}

          <TextField
            label="Amount" type="number" variant="standard" value={newEntry.amount}
            onChange={e => setNewEntry(ne => ({ ...ne, amount: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEntry}>Cancel</Button>
          <Button onClick={saveEntry} variant="contained">
            {entryEditIndex >= 0 ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Purchase modal */}
      <PurchaseDialog
        open={purchaseOpen}
        onClose={() => setPurchaseOpen(false)}
        billDate={billDate}
        onAdded={addEntryFromPurchase}
      />
    </Box>
  );
}
